import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { Reward } from 'shared/types';
import { i18n } from '../i18n/i18n';
import { useLanguage } from '../context/LanguageContext';
import { FEATURES } from '../constants/features';
import AntMascot from './AntMascot';
import { KidButton, KidCard, KidChip, KidProgress, KidSectionHeading, StarChip } from './kid/KidUI';
import { MyPocketMoney } from './pocket/PocketMoney';
import { Star } from './kid/KidIcon';
import { StoredPicture } from './kid/StoredPicture';
import { isMyIndividualReward, pluralKey } from '../utils/starGoal';
import { showToast } from './kid/Toast';

/**
 * Rewards (kid-first redesign step 4 — mock-ups phone-08 / tablet-*-07).
 *
 * Stars are never spent: each reward counts the stars earned since its own start date
 * (progress_start_at → eligible_points from the server), so "Counting stars since …" is shown
 * per reward. One currency on child screens (D3): stars. Family rewards are got by a grown-up.
 */

const starsText = (n: number) => i18n.t(pluralKey('common.stars', n)).replace('{{n}}', String(n));

const Rewards: React.FC = () => {
  const { current_user, current_family, claimReward } = useAppContext();
  const { current_language } = useLanguage();
  const [showAllClaimed, setShowAllClaimed] = useState(false);
  const [claimingId, setClaimingId] = useState<string | null>(null);

  if (!current_user || !current_family) {
    return <div className="text-center p-8">{i18n.t('common.loading')}</div>;
  }

  const isAdmin = !!(current_user as any).is_admin;
  const me = current_family.members.find((m: any) => m.id === current_user.id);
  const myStars = Number(me?.points ?? current_user.points ?? 0);

  // Mine (individual, for me or for any member) + the family's.
  const open = current_family.rewards.filter(
    (r: Reward) => !r.is_claimed && (isMyIndividualReward(r, current_user.id) || r.target_type === 'family'),
  );
  const progress = (r: Reward) => {
    const have = Math.max(0, Number(r.eligible_points ?? 0));
    const need = Number(r.points_required) || 0;
    return { have: Math.min(have, need), need, remaining: Math.max(0, need - have) };
  };
  const ready = open.filter((r) => progress(r).remaining === 0).sort((a, b) => a.points_required - b.points_required);
  const saving = open.filter((r) => progress(r).remaining > 0).sort((a, b) => progress(a).remaining - progress(b).remaining);

  const claimed = current_family.rewards
    .filter((r: Reward) => r.is_claimed && (r.claimed_by === current_user.id || r.target_type === 'family' || isAdmin))
    .sort((a, b) => ((b.claimed_at || b.created_at)?.getTime?.() ?? 0) - ((a.claimed_at || a.created_at)?.getTime?.() ?? 0));

  const handleClaim = async (reward: Reward) => {
    if (claimingId) return;
    setClaimingId(reward.id);
    try {
      await claimReward(reward.id, 'Claimed successfully!');
    } catch (error: any) {
      showToast({ message: error?.message || i18n.t('rewards.claim_failed'), tone: 'error' });
    } finally {
      setClaimingId(null);
    }
  };

  const since = (r: Reward) =>
    r.progress_start_at
      ? i18n.t('rewards.counting_since').replace('{{date}}', new Date(r.progress_start_at).toLocaleDateString(current_language, { day: 'numeric', month: 'short' }))
      : null;

  const readyCard = (reward: Reward) => {
    const canGet = reward.target_type === 'individual' || isAdmin;
    return (
      <KidCard key={reward.id} className="!border-kid-green dark:!border-kid-green-light flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <StoredPicture imageId={reward.header_image} label={reward.title} size={80} tint="peach" fit="cover" />
          <div className="min-w-0 flex-1">
            <h3 className="font-kid-heading font-extrabold text-2xl leading-tight break-words">{reward.title}</h3>
            <div className="flex flex-wrap gap-2 mt-2">
              <StarChip label={starsText(reward.points_required)} />
              {reward.target_type === 'family' && <KidChip>{i18n.t('rewards.target_whole_family')}</KidChip>}
            </div>
          </div>
        </div>
        {reward.description && <p className="text-base">{reward.description}</p>}
        {canGet ? (
          <KidButton size="lg" fullWidth disabled={claimingId === reward.id} onClick={() => handleClaim(reward)}>
            {claimingId === reward.id ? '…' : i18n.t('rewards.get_it')}
          </KidButton>
        ) : (
          <p className="text-center font-bold text-base">{i18n.t('rewards.grownup_gets')}</p>
        )}
      </KidCard>
    );
  };

  const savingRow = (reward: Reward) => {
    const p = progress(reward);
    return (
      <KidCard key={reward.id} className="flex items-center gap-4 !py-4">
        <StoredPicture imageId={reward.header_image} label={reward.title} size={64} fit="cover" />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="font-bold text-lg leading-tight truncate">{reward.title}</h3>
            <span className="shrink-0 font-bold text-base">{i18n.t('rewards.n_more').replace('{{n}}', String(p.remaining))}</span>
          </div>
          <KidProgress value={p.have} max={p.need} color={reward.target_type === 'family' ? 'family' : 'green'} size="sm" className="mt-2" label={reward.title} />
          <div className="flex flex-wrap items-center gap-x-3 mt-1.5 text-sm">
            {reward.target_type === 'family' && <span className="font-bold">{i18n.t('rewards.target_whole_family')}</span>}
            {since(reward) && <span>{since(reward)}</span>}
          </div>
        </div>
      </KidCard>
    );
  };

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 md:px-8 py-4 md:py-7 text-kid-text dark:text-kid-text-dark" lang={current_language}>
      <h1 className="font-kid-heading font-extrabold text-3xl md:text-4xl mb-5">{i18n.t('rewards.title')}</h1>
      {/* A child's own pocket money this month (2026-10-14) */}
      {!(current_user as any).is_admin && <MyPocketMoney />}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[400px_minmax(0,1fr)] lg:gap-8 lg:items-start">
        {/* star total */}
        <KidCard tone="peach" className="flex items-center gap-4 lg:flex-col lg:text-center lg:py-8">
          <Star size={56} className="lg:w-24 lg:h-24" />
          <div>
            <span className="font-kid-heading font-extrabold text-5xl lg:text-7xl leading-none">{myStars}</span>{' '}
            <span className="font-bold text-xl">{i18n.t('rewards.your_stars_label')}</span>
            {FEATURES.coins && (
              <p className="text-sm mt-1">🪙 {(current_user as any).coins ?? current_user.stars ?? 0} {i18n.t('rewards.your_coins')}</p>
            )}
          </div>
        </KidCard>

        <div className="flex flex-col gap-6">
          {open.length === 0 && (
            <KidCard tone="mint" className="flex items-center gap-4">
              <AntMascot pose="wave" className="w-16 h-16 shrink-0" />
              <p className="font-bold text-lg">{i18n.t('rewards.nothing_yet')}</p>
            </KidCard>
          )}

          {ready.length > 0 && (
            <section className="flex flex-col gap-3">
              <KidSectionHeading>{i18n.t('rewards.ready_to_get')}</KidSectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{ready.map(readyCard)}</div>
            </section>
          )}

          {saving.length > 0 && (
            <section className="flex flex-col gap-3">
              <KidSectionHeading>{i18n.t('rewards.keep_saving')}</KidSectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{saving.map(savingRow)}</div>
            </section>
          )}

          {claimed.length > 0 && (
            <section>
              <button
                type="button"
                onClick={() => setShowAllClaimed((v) => !v)}
                aria-expanded={showAllClaimed}
                className="w-full min-h-[56px] flex items-center gap-3 px-4 rounded-kid-card bg-kid-bg dark:bg-kid-bg-dark font-bold text-lg text-left"
              >
                <span aria-hidden>🏆</span>
                <span className="flex-1">{i18n.t(pluralKey('rewards.got', claimed.length)).replace('{{n}}', String(claimed.length))}</span>
                <span className="text-kid-green-shadow dark:text-green-300">
                  {showAllClaimed ? i18n.t('rewards.show_fewer') : i18n.t('rewards.see_all')} <span aria-hidden>{showAllClaimed ? '▲' : '›'}</span>
                </span>
              </button>
              {showAllClaimed && (
                <ul className="mt-2 flex flex-col gap-2">
                  {claimed.map((r) => (
                    <li key={r.id} className="flex items-center gap-3 px-4 py-3 rounded-kid-tile bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark">
                      <span className="text-kid-green-shadow dark:text-green-300 font-bold" aria-hidden>✓</span>
                      <span className="flex-1 font-bold truncate">{r.title}</span>
                      <span className="text-sm">{r.claimed_at ? r.claimed_at.toLocaleDateString(current_language) : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default Rewards;
